import filesService from "@/pages/FilePage/services/files.service";
import { UploadFileService } from "@/services/UploadFileService";
import { ofType } from "redux-observable";
import { validateFileSecurity } from "@/config/file-security.config";
import { EMPTY, forkJoin, from, merge, of } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
} from "rxjs/operators";
import { store } from "..";
import type { RootEpic } from "../types";
import {
  updateTaskStatus,
  uploadFileActions,
} from "./uploadFileSlice";
import { ConversionApi } from "@/api/conversion.api";
import { fileStatusActions } from "../convertFile/convertFile.slice";
import { DriveService } from "@/services/DriveService";
import { fileActions } from "../file";
import { folderUploadActions } from "../folderUpload";

const SINGLE_PART_THRESHOLD = 8 * 1024 * 1024;

// Global map to track active uploads and their cancellation functions
const activeUploads = new Map<
  string,
  {
    cancel: () => void;
    unsubscribes: (() => void)[];
  }
>();

// ========================================
// EPIC 1: Check if files exist
// ========================================
// Business logic only - NO UI, NO Modal, NO DOM
export const checkFileExistsEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(uploadFileActions.checkFileExistsRequest.type),
    concatMap(
      (action: ReturnType<typeof uploadFileActions.checkFileExistsRequest>) => {
        const {
          files,
          project,
          parentFolderId,
          companyId,
          teamId,
          isOrganization,
          __queueFileName, // Extract queue fileName if exists
          uniqueIds,
        } = action.payload;

        // Log uniqueIds for debugging
        console.log("🔍 [checkFileExistsEpic] Processing batch:", {
          fileCount: files.length,
          uniqueIds,
        });

        // Tách file thành 2 nhóm: valid và blocked (sử dụng config tập trung)
        const validFiles: File[] = [];
        const blockedFiles: File[] = [];

        files.forEach((file: File) => {
          validFiles.push(file);
        });
        //   const validation = validateFileSecurity(file.name, file.type);
          
        //   if (!validation.isValid) {
        //     blockedFiles.push(file);
        //   } else {
        //     validFiles.push(file);
        //   }
        // });

        // Dispatch action để thêm blocked files vào status list với failed status
        if (blockedFiles.length > 0) {          
          blockedFiles.forEach((file) => {
            store.dispatch(uploadFileActions.addFileToStatusList({ file }));
            store.dispatch(uploadFileActions.addFileToUploadModalStatusList({ file }));
            
            // Mark as failed immediately
            store.dispatch(
              uploadFileActions.updateFileProgress({
                fileName: file.name,
                progress: -1,
              })
            );
            store.dispatch(
              uploadFileActions.updateUploadModalFileProgress({
                fileName: file.name,
                progress: -1,
              })
            );
            
            // Set status to failed with custom message
            const updatedFile = {
              ...file,
              status: "failed",
              errorMessage: "File type not supported for security reasons"
            };
            
            // Add to abortedFiles to prevent any further processing
            store.dispatch(
              uploadFileActions.cancelPendingFiles({
                fileNames: [file.name],
                uniqueIds: uniqueIds,
              })
            );
            
            // Dispatch completeUploadSequenceFailure to trigger queue continuation
            store.dispatch(
              uploadFileActions.completeUploadSequenceFailure({
                file,
                fileName: file.name,
                message: "File blocked for security reasons",
                uniqueId: uniqueIds?.find((id) => id.includes(file.name)),
              })
            );
          });
        }

        // Nếu không có file hợp lệ, return success với empty array
        if (validFiles.length === 0) {
          return of(
            uploadFileActions.checkFileExistsSuccess({
              files: [],
              project,
              existingFiles: [],
            })
          );
        }

        const folderId =
          parentFolderId || localStorage.getItem("folderId") || undefined;

        // Chỉ check cho valid files
        const fileCheckRequests = validFiles.map((file: File) =>
          filesService
            .checkFileExists(project, file.name, folderId, companyId)
            .pipe(map((checkResult) => ({ file, checkResult })))
        );

        return forkJoin(fileCheckRequests).pipe(
          mergeMap((results: any[]) => {
            const existingFiles = results
              .filter(
                ({ checkResult }) =>
                  checkResult.exists &&
                  (checkResult.fileId || checkResult.document?.fileId)
              )
              .map(({ file, checkResult }) => ({
                file,
                fileId:
                  checkResult.fileId ??
                  Number(checkResult.document?.fileId ?? 0),
                currentVersion: checkResult.currentVersion || 1,
                version: checkResult.version,
              }));

            // Build parentFolderIdMap from file metadata
            const parentFolderIdMap: Record<string, string> = {};
            validFiles.forEach((file) => {
              const pid = (file as any).__parentFolderId;
              if (pid) parentFolderIdMap[file.name] = pid;
            });

            // ✅ NO CONFLICT: Upload immediately
            if (existingFiles.length === 0) {
              const uploadPayload: any = {
                files: validFiles,
                project,
                parentFolderId,
                parentFolderIdMap,
                companyId,
                teamId,
                isOrganization,
                uniqueIds,
              };
              
              // Only add __queueFileName if it exists (folder upload)
              if (__queueFileName) {
                uploadPayload.__queueFileName = __queueFileName;
              }
              
              return of(
                uploadFileActions.uploadFilesRequest(uploadPayload),
                uploadFileActions.checkFileExistsSuccess({
                  files: validFiles,
                  project,
                  existingFiles: [],
                })
              );
            }

            // ✅ CHECK FOR SAVED PREFERENCE: Apply automatically if exists
            const conflictPreference = state$.value.uploadFile.conflictPreference;
            if (conflictPreference) {
              // Auto-apply preference without showing modal
              if (conflictPreference === 'overwrite') {
                const fileVersionMap: Record<string, string | undefined> = {};
                const fileIdMap: Record<string, number | undefined> = {};

                existingFiles.forEach(({ file, version, fileId }: any) => {
                  fileVersionMap[file.name] = version;
                  fileIdMap[file.name] = fileId;
                });

                const uploadPayload: any = {
                  files: validFiles,
                  project,
                  parentFolderId,
                  parentFolderIdMap,
                  fileVersionMap,
                  fileIdMap,
                  companyId,
                  teamId,
                  isOrganization,
                  uniqueIds,
                };
                
                if (__queueFileName) {
                  uploadPayload.__queueFileName = __queueFileName;
                }

                return of(
                  uploadFileActions.uploadFilesRequest(uploadPayload),
                  uploadFileActions.checkFileExistsSuccess({
                    files: validFiles,
                    project,
                    existingFiles,
                  })
                );
              } 
              
              if (conflictPreference === 'createNew') {
                const uploadPayload: any = {
                  files: validFiles,
                  project,
                  parentFolderId,
                  parentFolderIdMap,
                  companyId,
                  teamId,
                  isOrganization,
                  uniqueIds,
                };
                
                if (__queueFileName) {
                  uploadPayload.__queueFileName = __queueFileName;
                }
                
                return of(
                  uploadFileActions.uploadFilesRequest(uploadPayload),
                  uploadFileActions.checkFileExistsSuccess({
                    files: validFiles,
                    project,
                    existingFiles: [],
                  })
                );
              }

              if (conflictPreference === "cancel") {
                const cancelledCount = validFiles.length;

                const actions: any[] = [
                  // giảm total file trong folder upload
                  folderUploadActions.setTotalFiles(-cancelledCount),

                  // vẫn phải success để flow checkFileExists kết thúc
                  uploadFileActions.checkFileExistsSuccess({
                    files: [],
                    project,
                    existingFiles,
                  }),
                ];

                files.forEach((file: File) => {
                  const uniqueId = (file as any).__uniqueId;
                  actions.push(
                    uploadFileActions.completeUploadSequenceFailure({
                      file,
                      fileName: file.name,
                      uniqueId,
                      message: "User cancelled upload"
                    })
                  );
                })


                return from(actions);
              }
            }

            // ⚠️ CONFLICT DETECTED: Dispatch action to show UI confirmation
            // Add conflict to queue instead of showing modal directly
            const conflictMeta: any = {
              parentFolderId,
              parentFolderIdMap,
              companyId,
              teamId,
              isOrganization,
              uniqueIds, // ✅ Pass uniqueIds to maintain tracking through conflict resolution
            };
            
            // Only add __queueFileName if it exists (folder upload)
            if (__queueFileName) {
              conflictMeta.__queueFileName = __queueFileName;
            }
            
            return of(
              uploadFileActions.enqueueConflict({
                files: validFiles,
                project,
                existingFiles,
                meta: conflictMeta,
              }),
              uploadFileActions.processNextConflict()
            );
          }),

          catchError((error) => {
            console.error("[checkFileExistsEpic] Error:", error);
            return of(
              uploadFileActions.checkFileExistsFailure({
                message: error.message,
              })
            );
          })
        );
      }
    )
  );

// ========================================
// EPIC 1.5: Process Conflict Queue
// ========================================
// Listens to processNextConflict and dispatches showFileConflictConfirm if there's an activeConflict
export const processConflictQueueEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(uploadFileActions.processNextConflict.type),
    mergeMap(() => {
      const state = state$.value;
      const { activeConflict, conflictPreference } = state.uploadFile;

      if (!activeConflict) {
        console.error('[processConflictQueueEpic] No active conflict, skipping');
        return EMPTY;
      }

      const { files, project, existingFiles, meta } = activeConflict;
      const {
        parentFolderId,
        parentFolderIdMap,
        companyId,
        teamId,
        isOrganization,
        __queueFileName,
      } = meta;

      // ✅ CHECK FOR SAVED PREFERENCE: Apply automatically if exists
      if (conflictPreference) {
        
        // Auto-apply preference without showing modal
        if (conflictPreference === 'overwrite') {
          const fileVersionMap: Record<string, string | undefined> = {};
          const fileIdMap: Record<string, number | undefined> = {};

          existingFiles.forEach(({ file, version, fileId }: any) => {
            fileVersionMap[file.name] = version;
            fileIdMap[file.name] = fileId;
          });

          const uploadPayload: any = {
            files,
            project,
            parentFolderId,
            parentFolderIdMap,
            fileVersionMap,
            fileIdMap,
            companyId,
            teamId,
            isOrganization,
            uniqueIds: meta.uniqueIds, // ✅ Pass uniqueIds from meta
          };
          
          if (__queueFileName) {
            uploadPayload.__queueFileName = __queueFileName;
          }

          return of(
            uploadFileActions.uploadFilesRequest(uploadPayload),
            uploadFileActions.checkFileExistsSuccess({
              files,
              project,
              existingFiles,
            }),
            uploadFileActions.resolveCurrentConflict(),
            uploadFileActions.processNextConflict()
          );
        } 
        
        if (conflictPreference === 'createNew') {
          const uploadPayload: any = {
            files,
            project,
            parentFolderId,
            parentFolderIdMap,
            companyId,
            teamId,
            isOrganization,
            uniqueIds: meta.uniqueIds, // ✅ Pass uniqueIds from meta
          };
          
          if (__queueFileName) {
            uploadPayload.__queueFileName = __queueFileName;
          }
          
          return of(
            uploadFileActions.uploadFilesRequest(uploadPayload),
            uploadFileActions.checkFileExistsSuccess({
              files,
              project,
              existingFiles: [],
            }),
            uploadFileActions.resolveCurrentConflict(),
            uploadFileActions.processNextConflict()
          );
        }

        if (conflictPreference === "cancel") {
          const cancelledCount = files.length;
          const actions: any[] = [
            folderUploadActions.setTotalFiles(-cancelledCount),
            uploadFileActions.checkFileExistsSuccess({
              files: [],
              project,
              existingFiles,
            })
          ];
          
          // Dispatch failure for EACH cancelled file to trigger queue continuation
          files.forEach((file: File) => {
            const uniqueId = (file as any).__uniqueId;
            actions.push(
              uploadFileActions.completeUploadSequenceFailure({
                file,
                fileName: file.name,
                uniqueId,
                message: "User cancelled upload (auto-applied preference)"
              })
            );
          });
          
          // Resolve current conflict and process next one
          actions.push(uploadFileActions.resolveCurrentConflict());
          actions.push(uploadFileActions.processNextConflict());
          
          return from(actions);
        }
      }

      return of(
        uploadFileActions.showFileConflictConfirm({
          files: activeConflict.files,
          project: activeConflict.project,
          existingFiles: activeConflict.existingFiles,
          meta: activeConflict.meta,
        })
      );
    })
  );

// ========================================
// EPIC 2: Handle user's conflict resolution choice
// ========================================
// Listens to the action dispatched from the UI Modal component
export const fileConflictConfirmResultEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.fileConflictConfirmResult.type),
    mergeMap(
      (
        action: ReturnType<typeof uploadFileActions.fileConflictConfirmResult>
      ) => {
        const { action: userChoice, meta } = action.payload;
        const {
          files,
          project,
          existingFiles,
          parentFolderId,
          parentFolderIdMap,
          companyId,
          teamId,
          isOrganization,
          __queueFileName, // Extract queue fileName from meta
        } = meta;

        // User cancelled - do nothing but trigger queue continuation for folder uploads
        if (userChoice === "cancel") {
          const cancelledCount = files.length;
          const actions: any[] = [
            folderUploadActions.setTotalFiles(-cancelledCount),
            uploadFileActions.checkFileExistsSuccess({
              files: [],
              project,
              existingFiles,
            })
          ];
          
          // Dispatch failure for EACH cancelled file to trigger queue continuation
          files.forEach((file: File) => {
            const uniqueId = (file as any).__uniqueId;
            actions.push(
              uploadFileActions.completeUploadSequenceFailure({
                file,
                fileName: file.name,
                uniqueId,
                message: "User cancelled upload"
              })
            );
          });
          
          // Resolve current conflict and process next one
          actions.push(uploadFileActions.resolveCurrentConflict());
          actions.push(uploadFileActions.processNextConflict());
          
          return from(actions);
        }

        // User chose to overwrite - use existing fileId and version
        if (userChoice === "overwrite") {
          const fileVersionMap: Record<string, string | undefined> = {};
          const fileIdMap: Record<string, number | undefined> = {};

          existingFiles.forEach(({ file, version, fileId }: any) => {
            if (version) fileVersionMap[file.name] = version;
            if (fileId) fileIdMap[file.name] = fileId;
          });

          // ✅ Extract uniqueIds from meta if available
          const uniqueIdsFromMeta = (meta as any).uniqueIds;

          const uploadPayload: any = {
            files,
            project,
            parentFolderId,
            parentFolderIdMap,
            fileVersionMap,
            fileIdMap,
            companyId,
            teamId,
            isOrganization,
            uniqueIds: uniqueIdsFromMeta, // ✅ Pass uniqueIds
          };
          
          // Only add __queueFileName if it exists (folder upload)
          if (__queueFileName) {
            uploadPayload.__queueFileName = __queueFileName;
          }

          return of(
            uploadFileActions.uploadFilesRequest(uploadPayload),
            uploadFileActions.checkFileExistsSuccess({
              files,
              project,
              existingFiles,
            }),
            uploadFileActions.resolveCurrentConflict(),
            uploadFileActions.processNextConflict()
          );
        }

        // User chose to create new - upload as new file (no version/fileId)
        if (userChoice === "createNew") {
          // Extract uniqueIds from meta if available
          const uniqueIdsFromMeta = (meta as any).uniqueIds;

          const uploadPayload: any = {
            files,
            project,
            parentFolderId,
            parentFolderIdMap,
            companyId,
            teamId,
            isOrganization,
            uniqueIds: uniqueIdsFromMeta, // ✅ Pass uniqueIds
          };
          
          // Only add __queueFileName if it exists (folder upload)
          if (__queueFileName) {
            uploadPayload.__queueFileName = __queueFileName;
          }
          
          return of(
            uploadFileActions.uploadFilesRequest(uploadPayload),
            uploadFileActions.checkFileExistsSuccess({
              files,
              project,
              existingFiles: [],
            }),
            uploadFileActions.resolveCurrentConflict(),
            uploadFileActions.processNextConflict()
          );
        }

        return EMPTY;
      }
    )
  );

export const uploadFilesEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(uploadFileActions.uploadFilesRequest.match),
    switchMap(
      (action: ReturnType<typeof uploadFileActions.uploadFilesRequest>) => {
        const {
          files,
          project,
          fileVersionMap,
          fileIdMap,
          parentFolderId,
          parentFolderIdMap,
          companyId,
          teamId,
          isOrganization,
          __queueFileName, // Extract queue fileName
        } = action.payload;

        // Thêm tất cả file vào cả 2 lists với progress = -1 khi uploadFilesRequest được gọi
        files.forEach((file) => {
          // ✅ USE existing uniqueId if available (from folderUploadEpic), otherwise generate new one
          let uniqueId = (file as any).__uniqueId;
          
          if (!uniqueId) {
            // Only generate if not already present
            uniqueId = `${file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            // Attach uniqueId to file object for tracking
            Object.defineProperty(file, "__uniqueId", {
              value: uniqueId,
              writable: false,
              enumerable: false,
              configurable: true,
            });
            
       
          } else {
        
          }

          store.dispatch(
            uploadFileActions.removeExistingFileRecords({ fileName: file.name })
          );
          store.dispatch(uploadFileActions.addFileToStatusList({ file, uniqueId })); // Jobs Modal
          
          // Calculate new version (current version + 1) for overwrite
          let displayVersion: string | undefined = undefined;
          if (fileVersionMap?.[file.name]) {
            const currentVersion = fileVersionMap[file.name];
            // Parse version string (could be "v2", "2", etc.) and increment
            const versionMatch = currentVersion?.match(/\d+/);
            if (versionMatch) {
              const newVersionNum = parseInt(versionMatch[0]) + 1;
              displayVersion = `${newVersionNum}`;
            }
          }
          
          store.dispatch(
            uploadFileActions.addFileToUploadModalStatusList({ 
              file, 
              uniqueId,
              version: displayVersion
            })
          ); // Upload Modal

          // Attach __parentFolderId to file object if exists in map
          if (parentFolderIdMap && parentFolderIdMap[file.name]) {
            if (!(file as any).__parentFolderId) {
              Object.defineProperty(file, "__parentFolderId", {
                value: parentFolderIdMap[file.name],
                writable: false,
                enumerable: false,
                configurable: true,
              });
            } else {

            }
          } else if (parentFolderId && !(file as any).__parentFolderId) {
            // Fallback: nếu không có trong map thì dùng parentFolderId chung
            Object.defineProperty(file, "__parentFolderId", {
              value: parentFolderId,
              writable: false,
              enumerable: false,
              configurable: true,
            });
          }
        });


        // Upload từng file song song với giới hạn concurrency = 10 files
        return from(files).pipe(
          mergeMap(
            (file) => {
              // Check if file has been aborted before starting upload
              const state = store.getState().uploadFile;
              if (state.abortedFiles.includes(file.name)) {
                console.warn('⚠️ [uploadFilesEpic$] File skipped - already aborted:', file.name);
                return of(uploadFileActions.completeUploadSequenceFailure({
                  fileName: file.name,
                  uniqueId: (file as any).__uniqueId,
                  message: 'File upload was aborted'
                }));
              }

              // const parentFolderId = (file as any).__parentFolderId;
              const useSinglePart = 0 < file.size && file.size <= SINGLE_PART_THRESHOLD;

              const upload$ = useSinglePart
                ? from(
                  uploadSinglePart(
                    file,
                    project,
                    fileVersionMap?.[file.name],
                    fileIdMap?.[file.name],
                    parentFolderId,
                    companyId,
                    teamId,
                    isOrganization,
                    (file as any).__uniqueId
                  )
                )
                : from(
                  uploadDirectToS3(
                    file,
                    project,
                    fileVersionMap?.[file.name],
                    fileIdMap?.[file.name],
                    parentFolderId,
                    companyId,
                    teamId,
                    isOrganization
                  )
                );

              return upload$.pipe(
                map((result) => {
                  return uploadFileActions.uploadFileResult({ file, result });
                })
              ).pipe(
                catchError((err) => {
                  console.error(`❌ [uploadFilesEpic$] Upload FAILED for: ${file.name}`, err);
                  
                  // Get uniqueId from file object
                  const uniqueId = (file as any).__uniqueId;
                  
                  // Dispatch failure to trigger queue continuation for folder uploads
                  store.dispatch(
                    uploadFileActions.completeUploadSequenceFailure({
                      file,
                      fileName: file.name,
                      uniqueId, // ✅ Pass uniqueId
                      message: err.message || 'Upload failed'
                    })
                  );
                  
                  return of(
                    uploadFileActions.uploadFileResult({
                      file,
                      result: { success: false, message: err.message },
                    })
                  );
                })
              );
            },
            10 // Giới hạn 10 files upload đồng thời
          )
        );
      }
    )
  );

async function uploadSinglePart(
  file: File,
  project: string,
  version?: string,
  existingFileId?: number,
  parentFolderId?: string,
  companyId?: string,
  teamId?: string,
  isOrganization?: boolean,
  uniqueId?: string,
  cancelled?: boolean,
  unsubscribeFunctions?: (() => void)[]
) {
  const fileName = file.name;
  const uploadTimestamp = Date.now();
  const uploadIndex = `${file.name}_${uploadTimestamp}`;

  // 1. Initialize single-part upload
  const startRes = await new Promise<{
    key: string;
    presignedUrl: string;
    fileId: string;
  }>((resolve, reject) => {
    if (cancelled) {
      reject(new Error("Upload cancelled"));
      return;
    }

    // Dispatch action to trigger epic
    store.dispatch(
      uploadFileActions.startSinglepartRequest({
        file,
        project,
        uploadIndex,
        version,
        existingFileId,
        companyId,
        uniqueId,
      })
    );

    // Listen for state changes
    let isResolved = false;
    const unsubscribe = store.subscribe(() => {
      if (isResolved) return;
      
      if (cancelled) {
        isResolved = true;
        unsubscribe();
        reject(new Error("Upload cancelled"));
        return;
      }

      const state = store.getState().uploadFile;

      // Check if file was aborted
      if (state.abortedFiles.includes(file.name)) {
        isResolved = true;
        unsubscribe();
        reject(new Error("Upload aborted"));
        return;
      }

      const result = state.startSinglepartResults.find(
        (r) => r.index === uploadIndex
      );
      if (result?.success) {
        isResolved = true;
        unsubscribe();
        resolve({
          key: result.key,
          presignedUrl: result.presignedUrl,
          fileId: result.fileId || "",
        });
      }
      if (result?.error) {
        isResolved = true;
        unsubscribe();
        reject(new Error(result.error));
      }
    });

    if (unsubscribeFunctions) {
      unsubscribeFunctions.push(unsubscribe);
    }
  });

  if (cancelled) throw new Error("Upload cancelled");

  const { key, presignedUrl, fileId } = startRes;

  console.log(`📤 [uploadSinglePart] Uploading to S3: ${fileName}`);

  // 2. Upload directly to S3 using presigned URL
  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Single-part upload failed with status: ${uploadResponse.status}`);
  }

  // Update progress to 100% after successful upload
  store.dispatch(
    uploadFileActions.updateFileProgress({ uniqueId, progress: 100 })
  );
  store.dispatch(
    uploadFileActions.updateUploadModalFileProgress({
      uniqueId,
      progress: 100,
    })
  );

  console.log(`✅ [uploadSinglePart] Upload successful: ${fileName}`);

  // 3. Call completeUploadSequence which will handle confirmUpload
  const config = store.getState().uploadFile.config;
  store.dispatch(
    uploadFileActions.completeUploadSequence({
      key,
      uploadId: null, // No uploadId for single-part
      fileId,
      parts: [], // No parts for single-part
      file,
      project,
      companyId: companyId || "",
      teamId,
      isOrganization,
      uniqueId,
      isSinglePart: true, // Flag to indicate single-part upload
    })
  );

  return {
    success: true,
    fileName: file.name,
    s3Key: key,
    fileSize: file.size,
  };
}

// Logic uploadDirectToS3: chia part, upload từng part, hoàn tất multipart
async function uploadDirectToS3(
  file: File,
  project: string,
  version?: string,
  existingFileId?: number,
  parentFolderId?: string,
  companyId?: string,
  teamId?: string,
  isOrganization?: boolean
) {
  const fileName = file.name;
  const uniqueId = (file as any).__uniqueId;
  
  // console.log(`📦 [uploadDirectToS3] START - File: ${fileName}`);
  // console.log(`   ├─ UniqueId: ${uniqueId || 'none'}`);
  // console.log(`   ├─ Size: ${file.size} bytes`);
  // console.log(`   ├─ Project: ${project}`);
  // console.log(`   ├─ Version: ${version || 'new'}`);
  // console.log(`   ├─ FileId: ${existingFileId || 'new'}`);
  // console.log(`   ├─ ParentFolderId: ${parentFolderId || 'root'}`);
  // console.log(`   ├─ __parentFolderId: ${(file as any).__parentFolderId || 'none'}`);
  // console.log(`   └─ isOrganization: ${isOrganization || false}`);
  
  // Validate file before processing
  if (!file || file.size === 0) {
    console.error(`❌ [uploadDirectToS3] SKIP - Invalid file: ${fileName}`, {
      exists: !!file,
      size: file?.size,
      uniqueId,
      reason: !file ? 'File object is null/undefined' : 'File size is 0'
    });
    console.error(`[uploadDirectToS3] Invalid file: ${fileName}`);
    store.dispatch(
      uploadFileActions.cancelPendingFiles({ 
        uniqueIds: uniqueId ? [uniqueId] : undefined,
        fileNames: [fileName], // Fallback for backward compatibility
      })
    );
    store.dispatch(
      uploadFileActions.completeUploadSequenceFailure({
        fileName,
        uniqueId,
        message: 'File is empty or invalid'
      })
    );
    throw new Error('File is empty or invalid');
  }
  
  // console.log(
  //   `📤 [uploadDirectToS3] Starting upload for ${fileName}, version:`,
  //   version,
  //   "fileId:",
  //   existingFileId,
  //   "parentFolderId:",
  //   parentFolderId
  // );

  // Ensure file has __parentFolderId if parentFolderId is provided
  if (parentFolderId && !(file as any).__parentFolderId) {
    Object.defineProperty(file, "__parentFolderId", {
      value: parentFolderId,
      writable: false,
      enumerable: false,
      configurable: true,
    });
    // console.log(`📁 [uploadDirectToS3] Set __parentFolderId for ${fileName}: ${parentFolderId}`);
  } else if ((file as any).__parentFolderId) {
    // console.log(`📁 [uploadDirectToS3] File ${fileName} already has __parentFolderId: ${(file as any).__parentFolderId}`);
  }
  let cancelled = false;
  const unsubscribeFunctions: (() => void)[] = [];

  // Setup cancellation tracking for this file
  activeUploads.set(fileName, {
    cancel: () => {
      cancelled = true;
      // Call all unsubscribe functions to clean up listeners
      unsubscribeFunctions.forEach((unsub) => {
        try {
          unsub();
        } catch (e) {
          console.warn("Error unsubscribing:", e);
        }
      });
      unsubscribeFunctions.length = 0;
    },
    unsubscribes: unsubscribeFunctions,
  });

  try {
    store.dispatch(
      uploadFileActions.clearMultipartResults({ fileName: file.name })
    );

    // Cập nhật progress = 0 khi bắt đầu quá trình upload cho cả 2 lists
    store.dispatch(
      uploadFileActions.updateFileProgress({ uniqueId, progress: 0 })
    );
    store.dispatch(
      uploadFileActions.updateUploadModalFileProgress({
        uniqueId,
        progress: 0,
      })
    );

    // 1. Khởi tạo multipart upload (dispatch và chờ kết quả qua state)
    const uploadTimestamp = Date.now();
    const uploadIndex = `${file.name}_${uploadTimestamp}`;

    const startRes = await new Promise<{
      key: string;
      uploadId: string;
      fileId: string;
    }>((resolve, reject) => {
      if (cancelled) {
        reject(new Error("Upload cancelled"));
        return;
      }

      // Dispatch action để trigger epic
      store.dispatch(
        uploadFileActions.startMultipartRequest({
          file,
          project,
          uploadIndex,
          version,
          existingFileId,
          companyId,
          uniqueId, // ✅ Pass uniqueId
        })
      );

      // Lắng nghe state thay đổi
      let isResolved = false; // ✅ Flag to prevent multiple resolves
      const unsubscribe = store.subscribe(() => {
        if (isResolved) return; // ✅ Skip if already resolved
        
        if (cancelled) {
          isResolved = true;
          unsubscribe();
          reject(new Error("Upload cancelled"));
          return;
        }

        const state = store.getState().uploadFile;

        // Check if file was aborted
        if (state.abortedFiles.includes(file.name)) {
          isResolved = true;
          unsubscribe();
          reject(new Error("Upload aborted"));
          return;
        }

        const result = state.startMultipartResults.find(
          (r) => r.index === uploadIndex
        );
        if (result?.success) {
          isResolved = true;
          unsubscribe();
          resolve({
            key: result.key,
            uploadId: result.uploadId,
            fileId: result.fileId || "",
          });
        }
        if (result?.error) {
          isResolved = true;
          unsubscribe();
          reject(new Error(result.error));
        }
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    if (cancelled) throw new Error("Upload cancelled");

    const { key, uploadId, fileId } = startRes;

    // 2. Chia part
    const PART_SIZE = 8 * 1024 * 1024;
    const totalParts = Math.ceil(file.size / PART_SIZE);
    const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

    // Lưu multipart attribute sau khi tính toán partNumbers
    store.dispatch(
      uploadFileActions.updateMultipartAttribute({
        uniqueId,
        key,
        uploadId,
        totalParts,
        fileId, // Lưu fileId để dùng cho abort
      })
    );

    const parts: { PartNumber: number; ETag: string }[] = [];

    // 3. Upload từng part song song with aggressive cancellation
    await promisePool(
      partNumbers,
      1,
      async (partNumber) => {
        // Immediate cancellation check
        if (cancelled) {
          throw new Error("Upload cancelled");
        }

        // Check if file has been aborted
        const currentState = store.getState().uploadFile;
        if (currentState.abortedFiles.includes(file.name)) {
          throw new Error("Upload aborted");
        }

        const start = (partNumber - 1) * PART_SIZE;
        const end = Math.min(start + PART_SIZE, file.size);
        const blob = file.slice(start, end);

        // 3.1. Lấy signed URL cho part with cancellation
        const signRes = await new Promise<{ url: string }>(
          (resolve, reject) => {
            if (cancelled) {
              reject(new Error("Upload cancelled"));
              return;
            }

            // Check abort status before dispatching
            const state = store.getState().uploadFile;
            if (state.abortedFiles.includes(file.name)) {
              reject(new Error("Upload aborted"));
              return;
            }

            store.dispatch(
              uploadFileActions.signPartRequest({ key, uploadId, partNumber })
            );

            let isResolved = false; // ✅ Flag to prevent multiple resolves
            const unsubscribe = store.subscribe(() => {
              if (isResolved) return; // ✅ Skip if already resolved
              
              if (cancelled) {
                isResolved = true;
                unsubscribe();
                reject(new Error("Upload cancelled"));
                return;
              }

              const state = store.getState().uploadFile;

              // Check abort status in subscription
              if (state.abortedFiles.includes(file.name)) {
                isResolved = true;
                unsubscribe();
                reject(new Error("Upload aborted"));
                return;
              }

              // ✅ Fix: Dùng uploadId trong index để tránh match nhầm kết quả cũ
              const result = state.signPartResults.find(
                (r) => r.index === `${key}_${uploadId}_${partNumber}`
              );
              if (result?.success) {
                isResolved = true;
                unsubscribe();
                resolve({ url: result.signedUrl });
              }
              if (result?.error) {
                isResolved = true;
                unsubscribe();
                reject(new Error(result.error));
              }
            });

            unsubscribeFunctions.push(unsubscribe);
          }
        );

        if (cancelled) throw new Error("Upload cancelled");

        const { url } = signRes;

        // Final check before uploading part
        if (
          cancelled ||
          store.getState().uploadFile.abortedFiles.includes(file.name)
        ) {
          throw new Error("Upload cancelled or aborted");
        }

        // 3.2. Upload part lên S3
        const put = await fetch(url, { method: "PUT", body: blob });
        if (!put.ok) throw new Error(`PUT part ${partNumber} failed`);
        const etag = put.headers.get("ETag") ?? "";
        parts[partNumber - 1] = { PartNumber: partNumber, ETag: etag };

        // Cập nhật progress sau khi hoàn thành upload part
        store.dispatch(
          uploadFileActions.uploadPartSuccess({
            uniqueId,
            partNumber: partNumber,
          })
        );
      },
      () => {
        // Cancellation function - more aggressive checking
        return (
          cancelled ||
          store.getState().uploadFile.abortedFiles.includes(file.name)
        );
      }
    );

    if (cancelled) throw new Error("Upload cancelled");

    // 4. Chuyển đổi parts sang format mới (lowercase)
    const formattedParts = parts.map((p) => ({
      partNumber: p.PartNumber,
      etag: p.ETag,
    }));

    // 5. Thực hiện luồng tuần tự với API mới: completeMultipart -> confirmUpload
    const config = store.getState().uploadFile.config;
    store.dispatch(
      uploadFileActions.completeUploadSequence({
        key,
        uploadId,
        fileId, // Truyền fileId từ init
        parts: formattedParts,
        file,
        project,
        companyId: companyId || "",
        teamId,
        isOrganization,
        uniqueId, // ✅ Pass uniqueId
        isSinglePart: false,
      })
    );

    return {
      success: true,
      fileName: file.name,
      s3Key: key,
      fileSize: file.size,
    };
  } catch (error) {
    console.log(`Upload failed for ${fileName}:`, error);
    store.dispatch(
      uploadFileActions.completeUploadSequenceFailure({
        fileName,
        uniqueId,
        message: "Sign part failed",
      })
    );
    throw error;
  } finally {
    // Clean up tracking
    activeUploads.delete(fileName);
  }
}

export const startSinglepartEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.startSinglepartRequest.type),
    mergeMap(
      (action: ReturnType<typeof uploadFileActions.startSinglepartRequest>) => {
        const config = store.getState().uploadFile.config;

        // Update FileAttribute
        store.dispatch(
          uploadFileActions.updateFileAttribute({
            fileName: action.payload.file.name,
            fileAttribute: {
              FileName: action.payload.file.name,
              contentType: action.payload.file.type,
              project: action.payload.project,
            },
          })
        );

        // Get parentId from file object or localStorage
        const parentId =
          (action.payload.file as any).__parentFolderId ||
          localStorage.getItem("folderId") ||
          undefined;

        // Get effective companyId
        const effectiveCompanyId = action.payload.companyId || localStorage.getItem("companyId") || "1";

        // Call new API: POST /api/upload/single/init
        return UploadFileService.Post.startSinglepart(
          action.payload.file,
          action.payload.project,
          action.payload.version,
          action.payload.existingFileId?.toString(),
          parentId,
          effectiveCompanyId
        ).pipe(
          map((res: any) => {
            // Response: { fileId, objectKey, bucketName, presignedUrl, uploadType, expiresIn }
            const { fileId, objectKey, presignedUrl } = res;

            console.log(`✅ [startSinglepartEpic$] Init successful for: ${action.payload.file.name}`, {
              fileId,
              objectKey,
            });

            return uploadFileActions.startSinglepartSuccess({
              presignedUrl,
              key: objectKey,
              fileId,
              file: action.payload.file,
              uploadIndex: action.payload.uploadIndex,
            });
          }),
          catchError((err) => {
            const uniqueId = (action.payload.file as any).__uniqueId;
            
            console.error(`❌ [startSinglepartEpic$] Init failed for: ${action.payload.file.name}`, err);
            
            store.dispatch(
              uploadFileActions.completeUploadSequenceFailure({
                file: action.payload.file,
                fileName: action.payload.file.name,
                uniqueId,
                message: err.response?.Message || 'Failed to start single-part upload',
                error: err.response,
              })
            );

            return of(
              uploadFileActions.startSinglepartFailure({
                file: action.payload.file,
                message: err.response,
              })
            );
          })
        );
      }
    )
  );


// 1. Khởi tạo multipart upload với API mới
export const startMultipartEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.startMultipartRequest.type),
    mergeMap(
      (action: ReturnType<typeof uploadFileActions.startMultipartRequest>) => {
        const config = store.getState().uploadFile.config;

        // Cập nhật FileAttribute
        store.dispatch(
          uploadFileActions.updateFileAttribute({
            fileName: action.payload.file.name,
            fileAttribute: {
              FileName: action.payload.file.name,
              contentType: action.payload.file.type,
              project: action.payload.project,
            },
          })
        );


        // Ưu tiên dùng parentFolderId từ file object, nếu không có thì dùng từ localStorage
        const parentId =
          (action.payload.file as any).__parentFolderId ||
          localStorage.getItem("folderId") ||
          undefined;

        // Gọi API mới: POST /api/upload/multipart/init?companyId=xxx&size=xxx
        const effectiveCompanyId = action.payload.companyId || localStorage.getItem("companyId") || "1";
        return UploadFileService.Post.startMultipart(
          action.payload.file,
          action.payload.project,
          action.payload.version, // Truyền version nếu có
          action.payload.existingFileId, // Truyền existingFileId nếu có
          parentId,
          action.payload.file.size, // Truyền size của file
          effectiveCompanyId // Truyền companyId từ payload
        ).pipe(
          map((res: any) => {
            // Response: { bucketName, fileId, uploadId, objectKey }
            const { uploadId, objectKey, fileId } = res;


            return uploadFileActions.startMultipartSuccess({
              uploadId,
              key: objectKey, // objectKey từ backend
              fileId, // fileId từ backend
              file: action.payload.file,
              uploadIndex: action.payload.uploadIndex,
            });
          }),
          catchError((err) => {
            const uniqueId = (action.payload.file as any).__uniqueId;
            
            store.dispatch(
              uploadFileActions.completeUploadSequenceFailure({
                file: action.payload.file,
                fileName: action.payload.file.name,
                uniqueId,
                message: err.response.Message || 'Failed to start multipart upload',
                error: err.response,
              })
            );

            return of(
              uploadFileActions.startMultipartFailure({
                file: action.payload.file,
                message: err.response,
              })
            );
          })
        );
      }
    )
  );

// 2. Ký từng part với API mới
export const signPartEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.signPartRequest.type),
    filter((action: ReturnType<typeof uploadFileActions.signPartRequest>) => {
      // Check if the file has been aborted before processing signPart request
      const state = store.getState().uploadFile;
      const fileStatus = state.fileStatusList.find(
        (f) => f.multipartAttribute?.key === action.payload.key
      );

      // If file is aborted, don't process the signPart request
      if (fileStatus && state.abortedFiles.includes(fileStatus.file.name)) {
        return false;
      }

      return true;
    }),
    mergeMap(
      (action: ReturnType<typeof uploadFileActions.signPartRequest>) => {
        const config = store.getState().uploadFile.config;
        // Gọi API mới: POST /api/upload/multipart/presign
        return UploadFileService.Post.signPart(
          action.payload.uploadId,
          action.payload.key, // objectKey
          action.payload.partNumber
        ).pipe(
          map((res: any) => {
          // Response: { presignedUrl, partNumber }
          const { presignedUrl, partNumber } = res;


          // Double-check abort status before processing response
          const state = store.getState().uploadFile;
          const fileStatus = state.fileStatusList.find(
            (f) => f.multipartAttribute?.key === action.payload.key
          );

          if (
            fileStatus &&
            !state.abortedFiles.includes(fileStatus.file.name)
          ) {
            // Thêm part vào fileStatusList only if file is not aborted
            store.dispatch(
              uploadFileActions.addPartToFile({
                fileName: fileStatus.file.name,
                part: {
                  url: presignedUrl,
                  partNumber: partNumber,
                },
              })
            );
          }

          return uploadFileActions.signPartSuccess({
            url: presignedUrl,
            partNumber: partNumber,
            index: `${action.payload.key}_${action.payload.uploadId}_${partNumber}`,
          });
        }),
        catchError((err) =>
          of(
            uploadFileActions.signPartFailure({
              ...action.payload,
              message: err.message,
            }),
          )
        )
      );
    })
  );

// 3. Upload từng part
export const uploadPartEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.uploadPartRequest.type),
    mergeMap((action: ReturnType<typeof uploadFileActions.uploadPartRequest>) =>
      from(
        fetch(action.payload.url, { method: "PUT", body: action.payload.blob })
      ).pipe(
        map((resp) => {
          if (!resp.ok) throw new Error("Upload part failed");
          const etag = resp.headers.get("ETag") ?? "";
          return uploadFileActions.uploadPartSuccess({
            ...action.payload,
            etag,
          });
        }),
        catchError((err) =>
          of(
            uploadFileActions.uploadPartFailure({
              ...action.payload,
              message: err.message,
            })
          )
        )
      )
    )
  );

// 4. Abort multipart upload epic
export const abortMultipartEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.abortMultipartRequest.type),
    mergeMap(
      (action: ReturnType<typeof uploadFileActions.abortMultipartRequest>) => {
        const state = store.getState().uploadFile;

        // Find the file being aborted
        const fileStatus = state.fileStatusList.find(
          (f) =>
            f.multipartAttribute?.key === action.payload.key &&
            f.multipartAttribute?.uploadId === action.payload.uploadId
        );

        // IMMEDIATELY cancel any active upload for this file
        if (fileStatus) {
          const activeUpload = activeUploads.get(fileStatus.file.name);
          if (activeUpload) {
 
            activeUpload.cancel();
          } else {
            console.log(`No active upload found for: ${fileStatus.file.name}`);
          }
        }

        // Lấy fileId từ multipartAttribute
        const fileId = fileStatus?.multipartAttribute?.fileId;

        if (!fileId) {
          console.error("❌ FileId is missing for abort operation");
          return of(
            uploadFileActions.abortMultipartFailure({
              key: action.payload.key,
              uploadId: action.payload.uploadId,
              message: "FileId is missing for abort operation",
            })
          );
        }

        // Gọi API mới: DELETE /api/upload/multipart/abort với body array
        return UploadFileService.Delete.abortMultipart([
          {
            fileId,
            uploadId: action.payload.uploadId,
            objectKey: action.payload.key,
          },
        ]).pipe(
          map((res) => {
            // After successful abort, check if file has baseFileId and cancel conversion
            if (fileStatus?.baseFileId) {
              // Stop any ongoing conversion polling
              store.dispatch(
                uploadFileActions.stopPollingTask({
                  baseFileId: fileStatus.baseFileId,
                })
              );

              // Remove file from converting files list
              store.dispatch(
                uploadFileActions.clearTaskNotConverted(fileStatus.baseFileId)
              );
            }

            // ✅ Dispatch failure to trigger queue continuation for folder uploads
            if (fileStatus) {
              store.dispatch(
                uploadFileActions.completeUploadSequenceFailure({
                  fileName: fileStatus.file.name,
                  uniqueId: fileStatus.uniqueId, // ✅ Use uniqueId from fileStatus
                  message: "Upload aborted by user",
                })
              );
            }

        

            return uploadFileActions.abortMultipartSuccess({
              key: action.payload.key,
              uploadId: action.payload.uploadId,
              result: res,
            });
          }),
          catchError((err) =>
            of(
              uploadFileActions.abortMultipartFailure({
                key: action.payload.key,
                uploadId: action.payload.uploadId,
                message: err.message,
              })
            )
          )
        );
      }
    )
  );

// NEW: Batch abort multipart upload epic
export const abortMultipartBatchEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.abortMultipartBatchRequest.type),
    mergeMap(
      (
        action: ReturnType<typeof uploadFileActions.abortMultipartBatchRequest>
      ) => {
        const state = store.getState().uploadFile;
        const { uploads } = action.payload;

        // Cancel all active uploads immediately
        uploads.forEach((upload) => {
          const fileStatus = state.fileStatusList.find(
            (f) =>
              f.multipartAttribute?.key === upload.key &&
              f.multipartAttribute?.uploadId === upload.uploadId
          );

          if (fileStatus) {
            console.log(
              `Attempting to cancel active upload for: ${fileStatus.file.name}`
            );
            const activeUpload = activeUploads.get(fileStatus.file.name);
            if (activeUpload) {
              console.log(
                `Found and cancelling active upload for: ${fileStatus.file.name}`
              );
              activeUpload.cancel();
            }
          }
        });

        // Prepare the uploads array with fileId from multipartAttribute
        const uploadsWithFileId = uploads
          .map((upload) => {
            const fileStatus = state.fileStatusList.find(
              (f) =>
                f.multipartAttribute?.key === upload.key &&
                f.multipartAttribute?.uploadId === upload.uploadId
            );

            const fileId =
              fileStatus?.multipartAttribute?.fileId || upload.fileId;

            if (!fileId) {
              console.warn(
                `⚠️ FileId is missing for upload with key: ${upload.key}`
              );
            }

            return {
              fileId: fileId || "",
              uploadId: upload.uploadId,
              objectKey: upload.key,
            };
          })
          .filter((u) => u.fileId); // Filter out uploads without fileId

        if (uploadsWithFileId.length === 0) {
          return of(
            uploadFileActions.abortMultipartBatchFailure({
              message: "No valid uploads to abort (missing fileId)",
            })
          );
        }

        // Call API with array of uploads
        return UploadFileService.Delete.abortMultipart(uploadsWithFileId).pipe(
          map((res) => {
            // After successful abort, stop polling and dispatch failure for each file
            uploads.forEach((upload) => {
              const fileStatus = state.fileStatusList.find(
                (f) =>
                  f.multipartAttribute?.key === upload.key &&
                  f.multipartAttribute?.uploadId === upload.uploadId
              );

              if (fileStatus?.baseFileId) {
                store.dispatch(
                  uploadFileActions.stopPollingTask({
                    baseFileId: fileStatus.baseFileId,
                  })
                );

                store.dispatch(
                  uploadFileActions.clearTaskNotConverted(fileStatus.baseFileId)
                );
              }

              // ✅ Dispatch failure to trigger queue continuation for folder uploads
              if (fileStatus) {
                store.dispatch(
                  uploadFileActions.completeUploadSequenceFailure({
                    fileName: fileStatus.file.name,
                    uniqueId: fileStatus.uniqueId, // ✅ Use uniqueId from fileStatus
                    message: "Upload aborted by user",
                  })
                );
              }
            });

      

            return uploadFileActions.abortMultipartBatchSuccess({
              uploads: uploads.map((u) => ({
                key: u.key,
                uploadId: u.uploadId,
              })),
              result: res,
            });
          }),
          catchError((err) =>
            of(
              uploadFileActions.abortMultipartBatchFailure({
                message: err.message,
              })
            )
          )
        );
      }
    )
  );

export const getTaskByBaseFileIdEpic = (action$: any) =>
  action$.pipe(
    ofType(uploadFileActions.getTaskByBaseFileId.type),
    switchMap(
      (action: ReturnType<typeof uploadFileActions.getTaskByBaseFileId>) => {
        const baseFileIds = Array.isArray(action.payload.baseFileId)
          ? action.payload.baseFileId
          : [action.payload.baseFileId];

        const validBaseFileIds = baseFileIds.filter(
          (id) => id && id !== "undefined"
        );


        if (validBaseFileIds.length === 0) {
          return EMPTY;
        }

        const requests = validBaseFileIds.map((baseFileId) =>
          ConversionApi.getTaskByBaseFileId(baseFileId).pipe(
            mergeMap((response: any) => {
              if (
                !response ||
                (Array.isArray(response) && response.length === 0)
              ) {
                return of(uploadFileActions.taskNotConverted({ baseFileId }));
              }
              const task = Array.isArray(response) ? response[0] : response;

              return [
                fileStatusActions.fileStatusFetched({
                  baseFileId,
                  data: response,
                }),
                updateTaskStatus({ baseFileId, task }),
              ];
            }),
            catchError((error) => {
              console.error("[Epic] Get task error:", error);
              return of(uploadFileActions.taskNotConverted({ baseFileId }));
            })
          )
        );

        return merge(...requests);
      }
    )
  );

// export const stopPollingWhenDoneEpic = (action$: any) =>
//   action$.pipe(
//     ofType(updateTaskStatus.type),
//     filter((action: ReturnType<typeof updateTaskStatus>) => {
//       const task = action.payload?.task;
//       return task && task.currentStep >= task.numberStep;
//     }),
//     map((action: ReturnType<typeof updateTaskStatus>) =>
//       uploadFileActions.stopPollingTask({ baseFileId: action.payload.baseFileId })
//     )
//   );

// export const uploadSuccessMessageEpic$: RootEpic = (action$) =>
//   action$.pipe(
//     ofType(uploadFileActions.completeUploadSequenceSuccess.type),
//     mergeMap(() => {
//       const preAction = uploadFileActions.setLastUploadAt(Date.now());

//       const folderId = localStorage.getItem("folderId");
//       const folderRootId = localStorage.getItem("folderRootId");

//       if (folderId && folderRootId && folderId === folderRootId) {
//         // message.success("You have successfully uploaded to My Drive");
//       }

//       return of(preAction);
//     })
//   );

// NEW: Epic xử lý luồng tuần tự completeUploadSequence
export const completeUploadSequenceEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.completeUploadSequence.type),
    mergeMap(
      (action: ReturnType<typeof uploadFileActions.completeUploadSequence>) => {
        const {
          key,
          uploadId,
          fileId,
          parts,
          file,
          project,
          companyId,
          teamId,
          isOrganization,
          isSinglePart,
        } = action.payload;
        
        const uniqueId = (file as any).__uniqueId;

        if (!fileId) {
          console.error(`❌ [completeUploadSequenceEpic$] Missing fileId for: ${file.name}`);
          return of(
            uploadFileActions.completeUploadSequenceFailure({
              fileName: file.name,
              message: "FileId is missing from init response",
            })
          );
        }

        // Get parentId from file object or localStorage
        const parentId =
          (file as any).__parentFolderId ||
          localStorage.getItem("folderId") ||
          undefined;

        // ========================================
        // BRANCH: Single-part vs Multi-part
        // ========================================
        const uploadObservable$ = isSinglePart
          ? // Single-part: Skip completeMultipart, go directly to confirmUpload
            of({ fileId })
          : // Multi-part: Call completeMultipart first
            UploadFileService.Post.completeMultipart(
              fileId,
              uploadId!,
              key,
              parts as any
            ).pipe(
              map((completeRes: any) => {
                console.log(`✅ [completeUploadSequenceEpic$] CompleteMultipart SUCCESS for: ${file.name}`, {
                  uniqueId,
                  fileId: completeRes.fileId,
                });
                return { fileId: completeRes.fileId };
              })
            );

        return uploadObservable$.pipe(
          mergeMap(({ fileId: completedFileId }) => {
            const orgId = localStorage.getItem("orgId") || "";
            

            return UploadFileService.Post.confirmUpload(
              {
                fileId: completedFileId,
                metadata: {
                  filename: file.name,
                  tags: [],
                  description: `Uploaded to ${project}`,
                  parentId: parentId,
                },
                ...(isOrganization && { isOrganization: true }),
              },
              companyId,
              orgId
            ).pipe(
              mergeMap((confirmRes: any) => {
                const baseFileIdFromConfirm = confirmRes.id;

                const successPayload: any = {
                  file,
                  fileId: Number(baseFileIdFromConfirm),
                  versionResult: confirmRes,
                  isOrganization,
                };

                return of(
                  uploadFileActions.completeUploadSequenceSuccess(successPayload)
                );
              })
            );
          }),
          catchError((err) => {
            console.error(`❌ [completeUploadSequenceEpic$] FAILED for: ${file.name}`, {
              uniqueId,
              error: err.message,
              uploadType: isSinglePart ? 'single-part' : 'multi-part',
            });
            
            return of(
              uploadFileActions.completeUploadSequenceFailure({
                fileName: file.name,
                uniqueId: (file as any).__uniqueId,
                message: err.message,
              })
            );
          })
        );
      }
    )
  );

export const getConfigEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.getConfig.type),
    mergeMap(() =>
      from(
        UploadFileService.Get.getConfig().pipe(
          map((res) => {
            const config = res || {};
            return uploadFileActions.setConfig({
              ...config,
            });
          }),
          catchError((_err) => of())
        )
      )
    )
  );

// promisePool with cancellation support
async function promisePool<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, idx: number) => Promise<R>,
  shouldCancel?: () => boolean
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  const errors: Error[] = [];
  let i = 0;
  let active = 0;
  let resolve!: (value: R[]) => void;
  let reject!: (reason?: any) => void;
  const done = new Promise<R[]>(
    (res, rej) => ((resolve = res), (reject = rej))
  );

  const next = () => {
    // Check for cancellation before processing more items
    if (shouldCancel && shouldCancel()) {
      reject(new Error("Operation cancelled"));
      return;
    }

    if (i === items.length && active === 0) {
      if (errors.length > 0) {
        console.warn(`[promisePool] ${errors.length} workers failed`);
      }
      return resolve(results);
    }
    
    while (active < limit && i < items.length) {
      // Check for cancellation before starting new work
      if (shouldCancel && shouldCancel()) {
        reject(new Error("Operation cancelled"));
        return;
      }

      const idx = i++;
      active++;
      worker(items[idx], idx)
        .then((r) => {
          results[idx] = r;
        })
        .catch((err) => {
          console.error(`[promisePool] Worker ${idx} failed:`, err);
          errors.push(err);
          results[idx] = null as any; // Mark as failed
        })
        .finally(() => {
          active--;
          next();
        });
    }
  };
  next();
  return done;
}

// Epic để tự động trigger extraction khi file upload + convert hoàn tất
// export const autoExtractMetadataEpic = (action$: any) =>
//   action$.pipe(
//     ofType(updateTaskStatus.type),
//     filter((action: ReturnType<typeof updateTaskStatus>) => {
//       const task = action.payload?.task;
//       const { baseFileId } = action.payload;

//       // Kiểm tra xem file đã được extract chưa
//       const state = store.getState();
//       const alreadyExtracted =
//         state.fileStatus.extractedFiles.includes(baseFileId);

//       if (alreadyExtracted) {

//         return false;
//       }

//       // Chỉ trigger khi task hoàn thành (currentStep >= numberStep)
//       return task && task.currentStep >= task.numberStep;
//     }),
//     mergeMap((action: ReturnType<typeof updateTaskStatus>) => {
//       const { baseFileId } = action.payload;

//       // Tìm file record từ uploadModalFileStatusList
//       const state = store.getState();
//       const fileRecord = state.uploadFile.uploadModalFileStatusList.find(
//         (f) => f.baseFileId === baseFileId
//       );

//       if (!fileRecord || !fileRecord.file) {
//         console.log(
//           `[Auto Extract] No file record found for baseFileId: ${baseFileId}`
//         );
//         return EMPTY;
//       }

//       const fileName = fileRecord.file.name;
//       const projectName = String(localStorage.getItem("projectName"));

//       // Chỉ auto-extract cho các file Revit (.rvt)
//       if (!fileName.toLowerCase().endsWith(".rvt")) {
//         console.log(`[Auto Extract] Skipping non-Revit file: ${fileName}`);
//         return EMPTY;
//       }

//       console.log(
//         `[Auto Extract] Triggering extraction for: ${fileName}, baseFileId: ${baseFileId}`
//       );

//       const payload = {
//         project: projectName,
//         revitFilePath: fileName,
//         s3Key: `${projectName}/${fileName}`,
//         databaseName: projectName,
//         facility: "jedons_facility_1",
//         fileId: baseFileId,
//       };

//       // Mark file as extracted và dispatch extraction request
//       return merge(
//         of(fileStatusActions.markFileAsExtracted({ baseFileId })),
//         of(extractionActions.extractMetadataRequest(payload))
//       );
//     }),
//     catchError((error) => {
//       console.error("[Auto Extract] Error:", error);
//       return EMPTY;
//     })
//   );

// Epic lấy thông tin sử dụng dung lượng của công ty
export const getCompanyStorageUsageEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(uploadFileActions.getCompanyStorageUsageRequest.type),
    switchMap(
      (
        action: ReturnType<
          typeof uploadFileActions.getCompanyStorageUsageRequest
        >
      ) => {
        const { companyId, labelId } = action.payload;
        return from(DriveService.checkCompanyRoot(companyId, labelId)).pipe(
          map((response) =>
            uploadFileActions.getCompanyStorageUsageSuccess(response)
          ),
          catchError((err) =>
            of(
              uploadFileActions.getCompanyStorageUsageFailure(
                err.message || "Error fetching company storage usage"
              )
            )
          )
        );
      }
    )
  );

// epic tinh storage size sau khi upload thanh cong
export const storageAfterUploadEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(uploadFileActions.completeUploadSequenceSuccess.match),
    mergeMap((action) => {
      const size = action.payload.file.size;
      const isOrganization = action.payload.isOrganization;

      // Nếu isOrganization = true, chỉ cập nhật company storage
      if (isOrganization) {
        return [uploadFileActions.updateCompanyStorageSize(size)];
      }

      return [
        fileActions.updateUserStorageSize(size),
        uploadFileActions.updateCompanyStorageSize(size),
      ];
    })
  );
// Gộp các epic lại
export const uploadFileEpics = [
  checkFileExistsEpic$, // Check file existence
  processConflictQueueEpic$, // Process conflict queue
  fileConflictConfirmResultEpic$, // Handle user's conflict resolution choice
  startMultipartEpic$,
  signPartEpic$,
  uploadPartEpic$,
  abortMultipartEpic$,
  abortMultipartBatchEpic$, // NEW: Batch abort epic
  completeUploadSequenceEpic$,
  // uploadSuccessMessageEpic$,
  uploadFilesEpic$,
  getConfigEpic$,
  getTaskByBaseFileIdEpic,
  // autoExtractMetadataEpic, // NEW: Auto-trigger extraction when upload + convert complete
  // stopPollingWhenDoneEpic,
  getCompanyStorageUsageEpic$, // NEW: Add company storage usage epic
  storageAfterUploadEpic$,
  startSinglepartEpic$,
];
